import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, ArrowRight, User, LogIn } from 'lucide-react';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import shell from '@/styles/authShell.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralClaim = () => {
  usePageMeta(PAGE_META.referralClaim);

  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) { setError('Invalid referral link'); setLoading(false); return; }

    // Store token for post-auth claiming
    sessionStorage.setItem('referral_token', token);

    fetch(`${API}/referral/${token}`)
      .then(res => { if (!res.ok) throw new Error('Referral not found'); return res.json(); })
      .then(data => {
        setReferral(data);
        // Cache prefill data so Signup/Login can prepopulate name & email
        try {
          sessionStorage.setItem('referral_prefill', JSON.stringify({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.referral_client_email || '',
            lawyerName: data.lawyerName || '',
            lawFirmName: data.lawFirmName || ''
          }));
        } catch (err) { /* ignore quota errors */ }
        setLoading(false);
      })
      .catch(() => { setError('This referral link is invalid or has expired.'); setLoading(false); });
  }, []);

  const handleCreateAccount = () => {
    navigate('/signup', { state: { fromReferral: true } });
  };

  const handleLogin = () => {
    navigate('/login', { state: { fromReferral: true } });
  };

  if (loading) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}>
          <p>Loading...</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shell.page}>
        <main className={shell.centre}>
          <div className={shell.inner}>
            <div className={shell.card}>
              <div className={shell.head}>
                <div className={`${shell.statusIcon} ${shell.statusError}`}>
                  <Scale size={32} aria-hidden="true" />
                </div>
                <h1 className={shell.title}>Referral Not Found</h1>
                <p className={shell.subtitle}>{error}</p>
              </div>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                onClick={() => navigate('/')}
                data-testid="referral-home-btn"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const alreadyClaimed = !!referral.client_id;

  return (
    <div className={shell.page} data-testid="referral-claim-page">
      <header className={shell.header}>
        <div className={shell.headerInner}>
          <button type="button" className={shell.logo} onClick={() => navigate('/')} aria-label="MyCaseLoan home">
            <Scale size={26} aria-hidden="true" />
            <span className={shell.logoText}>MyCaseLoan</span>
          </button>
        </div>
      </header>

      <main className={shell.centre}>
        <div className={shell.inner}>
          <div className={shell.card}>
            <div className={shell.head}>
              <div className={shell.mark}>
                <Scale size={28} aria-hidden="true" />
              </div>
              <h1 className={shell.title}>Your Attorney Has Invited You to Apply</h1>
              <p className={shell.subtitle}>
                <strong>{referral.lawyerName || 'Your attorney'}</strong>
                {referral.lawFirmName ? ` at ${referral.lawFirmName}` : ''} has invited you to
                apply for pre-settlement funding through MyCaseLoan.
              </p>
            </div>

            {alreadyClaimed ? (
              <>
                <div className={`${shell.banner} ${shell.bannerCentre}`}>
                  This application is already linked to an account.
                </div>
                <button
                  type="button"
                  className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                  onClick={handleLogin}
                  data-testid="referral-login-claimed-btn"
                >
                  <LogIn size={16} aria-hidden="true" /> Log In to View
                </button>
              </>
            ) : (
              <>
                <p className={shell.subtitle}>
                  To continue with your application, please create an account or log in to your
                  existing MyCaseLoan account.
                </p>
                <div className={`${shell.stack} ${shell.spacedTop}`}>
                  <button
                    type="button"
                    className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                    onClick={handleCreateAccount}
                    data-testid="referral-signup-btn"
                  >
                    <User size={18} aria-hidden="true" /> Create an Account <ArrowRight size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className={`${shared.btn} ${shared.btnQuiet} ${shell.fullBtn}`}
                    onClick={handleLogin}
                    data-testid="referral-login-btn"
                  >
                    <LogIn size={18} aria-hidden="true" /> I Already Have an Account
                  </button>
                </div>
              </>
            )}
          </div>

          <p className={shell.footNote}>
            &copy; {new Date().getFullYear()} MyCaseLoan. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
};

export default ReferralClaim;
